import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { platformAlert } from "../utils/platformAlert";
import { Image } from "expo-image";
import { Feather, MaterialIcons } from "@expo/vector-icons";
import { useColors } from "../hooks/useColors";
import { spacing } from "../theme";
import { Button } from "../components/ui/Button";
import { useCart } from "../hooks/useCart";
import { useDelivery } from "../hooks/useDelivery";
import { useNavigation, useFocusEffect, useIsFocused } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useDeliveryAddressContext } from "../contexts/DeliveryAddressContext";
import CartDeliveryMap from "../components/map/CartDeliveryMap";
import { supabase } from "../lib/supabase";
import type { VendorQuote, DeliveryAddress } from "../types/delivery";
import type { RootStackParamList } from "../types/navigation";
import type { ViewStyle } from "react-native";
import { useTranslation } from "react-i18next";
import { RouteProp, useRoute } from "@react-navigation/native";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { DELIVERY_CONFIG, DeliveryHelpers } from "../config/deliveryConfig";
import { usePostHog } from "../hooks/usePostHog";
import { trackEvent } from "../services/posthog";
import { formatPrice } from "../utils/format";
import { 
  isStoreCurrentlyOpen, 
  validateDeliveryTimeAgainstStoreHours,
  formatTimeDisplay,
  type StoreHours 
} from "../utils/storeHours";
import { useVendorHoursRealtime } from "../hooks/useVendorHoursRealtime";

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

interface CartItem {
  id: string;
  title: string;
  price: number;
  quantity: number;
  image: string;
  size?: string;
  product: {
    id: string;
    title: string;
    price: number;
    images: string[];
    size?: string;
    quantity: number;
    vendor_id: string;
    vendor: {
      id: string;
      store_name: string;
      street_line1: string;
      city: string;
      state: string;
      zip_code: string;
    };
  };
}


const calculateDeliveryTime = (dropoffEta: string | undefined): string => {
  if (!dropoffEta) return "Not available";

  try {
    // Parse the ISO 8601 timestamp
    const etaDate = new Date(dropoffEta);
    if (isNaN(etaDate.getTime())) return "Not available";

    const now = new Date();
    const diffMinutes = Math.round(
      (etaDate.getTime() - now.getTime()) / (1000 * 60)
    );

    // If the time difference is negative or too small, show a minimum time
    if (diffMinutes < 1) return "1-5 min";

    // For times less than 60 minutes
    if (diffMinutes < 60) {
      // Round to nearest 5 minutes for better UX
      const roundedMinutes = Math.ceil(diffMinutes / 5) * 5;
      return `${roundedMinutes} min`;
    }

    // For times more than 60 minutes
    const hours = Math.floor(diffMinutes / 60);
    const minutes = Math.round((diffMinutes % 60) / 5) * 5; // Round remaining minutes to nearest 5

    if (minutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${minutes}m`;
  } catch (error) {
    console.error("Error calculating delivery time:", error);
    return "Not available";
  }
};

const calculateDeliveryFee = (quote: VendorQuote | undefined): number => {
  if (!quote?.quote) return 0;
  return DeliveryHelpers.centsToeDollars(quote.quote.fee);
};

const createStyles = (colors: any) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    itemList: {
      flex: 1,
    },
    cartItem: {
      flexDirection: "row",
      padding: 15,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    itemContent: {
      flexDirection: "row",
      flex: 1,
    },
    itemImage: {
      width: 70,
      height: 70,
      borderRadius: 8,
      backgroundColor: colors.backgroundSecondary,
    },
    placeholderImage: {
      backgroundColor: colors.backgroundSecondary,
    },
    itemDetails: {
      flex: 1,
      marginLeft: 12,
    },
    itemTitleRow: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      marginBottom: 4,
    },
    itemTitle: {
      fontSize: 16,
      fontWeight: "500",
      marginBottom: 4,
      flex: 1,
      color: colors.text,
    },
    itemSize: {
      fontSize: 14,
      color: colors.textLight,
      marginBottom: 4,
    },
    itemPrice: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.primary,
      marginBottom: 8,
    },
    confirmationStatusIndicator: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: 4,
      gap: 4,
    },
    confirmationStatusText: {
      fontSize: 12,
      fontWeight: "500",
    },
    quantityControl: {
      flexDirection: "row",
      alignItems: "center",
    },
    quantityButton: {
      width: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: colors.backgroundSecondary,
      alignItems: "center",
      justifyContent: "center",
    },
    quantity: {
      paddingHorizontal: 10,
      fontSize: 16,
      color: colors.text,
    },
    singleUnitIndicator: {
      paddingHorizontal: 12,
      paddingVertical: 4,
      backgroundColor: colors.backgroundSecondary,
      borderRadius: 12,
    },
    singleUnitText: {
      fontSize: 12,
      color: colors.textLight,
      fontWeight: "500",
    },
    removeButton: {
      marginLeft: 10,
      alignSelf: "center",
      padding: 5,
    },
    vendorAvailabilityIndicator: {
      flexDirection: "row",
      alignItems: "center",
      borderRadius: 4,
      paddingHorizontal: 6,
      paddingVertical: 2,
      marginLeft: 8,
    },
    vendorAvailableIndicator: {
      backgroundColor: colors.primary,
    },
    vendorUnavailableIndicator: {
      backgroundColor: colors.error,
    },
    vendorAvailabilityText: {
      fontSize: 10,
      fontWeight: "500",
      color: colors.surface,
      marginLeft: 2,
    },
    vendorName: {
      fontSize: 14,
      color: colors.textLight,
      marginBottom: 4,
    },
    vendorAddress: {
      fontSize: 14,
      color: colors.textLight,
      marginBottom: 4,
    },
    emptyCart: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      paddingVertical: 60,
    },
    emptyCartText: {
      fontSize: 16,
      color: colors.textLight,
      textAlign: "center",
      marginTop: 20,
    },
    availabilityInfoContainer: {
      padding: 16,
      backgroundColor: colors.backgroundSecondary,
      marginBottom: 16,
      borderRadius: 8,
    },
    availabilityInfoTitle: {
      fontSize: 16,
      fontWeight: "600",
      marginBottom: 8,
      color: colors.text,
    },
    availabilityInfoText: {
      fontSize: 14,
      color: colors.text,
      marginBottom: 12,
    },
    deliverySection: {
      marginTop: 16,
      paddingHorizontal: 16,
      marginBottom: 100,
    },
    deliveryToggle: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      padding: 12,
      backgroundColor: colors.backgroundSecondary,
      borderRadius: 8,
    },
    deliveryToggleError: {
      backgroundColor: colors.errorBackground,
    },
    deliveryToggleContent: {
      flex: 1,
    },
    deliveryToggleText: {
      fontSize: 16,
      color: colors.text,
    },
    errorText: {
      fontSize: 12,
      color: colors.error,
      marginTop: 2,
    },
    deliveryPrice: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.primary,
      marginLeft: 8,
    },
    loader: {
      marginLeft: 8,
    },
    deliveryDetails: {
      marginTop: 12,
    },
    quoteDetail: {
      marginBottom: 8,
    },
    quoteError: {
      backgroundColor: colors.errorBackground,
      padding: 12,
      borderRadius: 6,
      borderLeftWidth: 3,
      borderLeftColor: colors.error,
    },
    pickupAddress: {
      fontSize: 14,
      color: colors.textLight,
    },
    deliveryTime: {
      fontSize: 14,
      color: colors.textLight,
    },
    quoteFee: {
      fontSize: 14,
      color: colors.textLight,
    },
    summaryContainer: {
      padding: 16,
      borderTopWidth: 1,
      borderTopColor: colors.border,
      backgroundColor: colors.surface,
      position: "absolute",
      bottom: 25,
      left: 0,
      right: 0,
    },
    sectionTitle: {
      fontSize: 18,
      fontWeight: "600",
      color: colors.text,
      marginBottom: 12,
    },
    summaryContent: {
      flex: 1,
    },
    summaryRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: 8,
    },
    summaryLabel: {
      fontSize: 14,
      color: colors.textLight,
    },
    summaryValue: {
      fontSize: 14,
      color: colors.text,
    },
    feeNote: {
      fontSize: 12,
      color: colors.textLight,
      fontStyle: "italic",
    },
    storeSelectorContainer: {
      padding: 16,
      backgroundColor: colors.backgroundSecondary,
      marginBottom: 16,
    },
    storeSelectorTitle: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.text,
      marginBottom: 12,
    },
    storeSelectorScroll: {
      flexDirection: "row",
    },
    storeOption: {
      backgroundColor: colors.surface,
      paddingVertical: 12,
      paddingHorizontal: 16,
      marginRight: 12,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
      minWidth: 120,
    },
    storeOptionSelected: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    storeOptionText: {
      fontSize: 14,
      fontWeight: "500",
      color: colors.text,
      marginBottom: 4,
    },
    storeOptionTextSelected: {
      color: colors.surface,
    },
    storeItemCount: {
      fontSize: 12,
      color: colors.textLight,
    },
    storeItemCountSelected: {
      color: colors.surface,
      opacity: 0.8,
    },
    selectedStoreInfo: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: colors.backgroundSecondary,
      paddingVertical: 8,
      paddingHorizontal: 16,
      marginHorizontal: 16,
      marginBottom: 16,
      borderRadius: 6,
      borderLeftWidth: 3,
      borderLeftColor: colors.primary,
    },
    selectedStoreText: {
      fontSize: 14,
      fontWeight: "500",
      color: colors.text,
      marginLeft: 8,
      flex: 1,
    },
    selectedStoreSubtext: {
      fontSize: 12,
      color: colors.textLight,
      marginLeft: 8,
    },
    divider: {
      height: 1,
      backgroundColor: colors.border,
      marginVertical: 12,
    },
    totalLabel: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.text,
    },
    totalValue: {
      fontSize: 18,
      fontWeight: "600",
      color: colors.primary,
    },
    checkoutSection: {
      padding: 16,
    },
    checkoutButton: {
      marginTop: 8,
    },
    totalRow: {
      marginTop: 12,
    },
    summary: {
      padding: 16,
      borderTopWidth: 1,
      borderTopColor: colors.border,
      backgroundColor: colors.surface,
    },
    sectionContainer: {
      marginBottom: 16,
    },
    emptyState: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      marginTop: 80,
    },
    emptyTitle: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: 12,
    },
    emptyText: {
      fontSize: 16,
      color: colors.textLight,
      marginBottom: 24,
    },
    browseButton: {
      padding: 16,
      backgroundColor: colors.primary,
      borderRadius: 8,
    },
    confirmationContainer: {
      backgroundColor: colors.backgroundSecondary,
      borderRadius: 8,
      padding: 16,
      marginVertical: 16,
      borderWidth: 1,
      borderColor: colors.border,
    },
    confirmationTitle: {
      fontSize: 18,
      fontWeight: "600",
      marginBottom: 8,
      color: colors.primary, // Change to green to match app theme
    },
    confirmationText: {
      fontSize: 14,
      color: colors.textLight,
      marginBottom: 16,
    },
    confirmationButton: {
      backgroundColor: colors.primary, // Change to green to match app theme
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: "center",
    },
    confirmationButtonText: {
      color: colors.white,
      fontWeight: "600",
      fontSize: 16,
    },
    progressContainer: {
      height: 6,
      backgroundColor: colors.border,
      borderRadius: 3,
      marginBottom: 16,
      overflow: "hidden",
    },
    progressBar: {
      height: "100%",
      backgroundColor: colors.primary, // Change to green to match app theme
    },
    sellerStatus: {
      flexDirection: "row",
      justifyContent: "space-between",
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    sellerName: {
      fontSize: 14,
      fontWeight: "500",
      color: colors.text,
    },
    sellerResponse: {
      fontSize: 14,
      fontWeight: "600",
    },
    sellerPending: {
      fontSize: 14,
      color: colors.textLight,
    },
    requestConfirmationButton: {
      backgroundColor: colors.secondary, // cyan
      paddingVertical: 16,
      borderRadius: 8,
      alignItems: "center",
      margin: 16,
    },
    requestConfirmationText: {
      color: colors.surface,
      fontWeight: "600",
      fontSize: 16,
    },
    disabledButton: {
      backgroundColor: colors.border,
    },
    checkoutButtonText: {
      color: colors.surface,
      fontWeight: "600",
      fontSize: 16,
    },
    actionButton: {
      paddingVertical: 16,
      borderRadius: 8,
      alignItems: "center",
      marginVertical: 16,
    },
    mainConfirmationButton: {
      backgroundColor: colors.primary, // Green for the main Request Confirmation action
    },
    confirmButton: {
      backgroundColor: colors.primary, // Green
    },
    buttonText: {
      color: colors.text,
      fontWeight: "600",
      fontSize: 16,
    },
    greenText: {
      color: colors.primary, // Green text color
      fontWeight: "600",
      fontSize: 16,
    },
    warningText: {
      fontSize: 12,
      color: colors.warning,
      marginTop: 2,
    },
    confirmedStatusContainer: {
      backgroundColor: colors.backgroundSecondary,
      borderRadius: 8,
      padding: 16,
      marginVertical: 8,
      borderLeftWidth: 3,
      borderLeftColor: colors.success,
    },
    confirmedStatusIndicator: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: 4,
      gap: 8,
    },
    confirmedStatusText: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.success,
    },
    confirmedStatusSubtext: {
      fontSize: 14,
      color: colors.textLight,
      marginLeft: 28,
    },
  });

export default function CartScreen() {
  console.log("CartScreen: Component mounted");
  const colors = useColors();
  const styles = createStyles(colors);
  const { t } = useTranslation();
  const navigation = useNavigation<NavigationProp>();
  const isFocused = useIsFocused();
  const { session } = useAuth();
  const posthog = usePostHog();

  const {
    cartItems: allCartItems,
    updateQuantity,
    removeItem,
    cancelConfirmedItem,
    clearCart,
    updateCartItem,
    hasConfirmedItems,
    hasPendingConfirmations,
    isCartLocked,
    refreshCart,
    isRemoving,
    isCancelling,
    isLoading: isCartLoading,
  } = useCart();

  // Track cart viewed when cart items are loaded
  useEffect(() => {
    if (!allCartItems || allCartItems.length === 0) return;

    try {
      const cartValue = allCartItems.reduce(
        (sum, item) => sum + (item.product?.price || 0) * (item.quantity || 0),
        0
      );

      trackEvent(
        "cart_viewed",
        {
          item_count: allCartItems.length,
          cart_value: Number(cartValue.toFixed(2)),
        },
        posthog || undefined
      );
    } catch (analyticsError) {
      console.warn("[CartScreen] Failed to track cart_viewed:", analyticsError);
    }
  }, [allCartItems, posthog]);

  const { getQuoteForCart, deliveryQuote, isLoading, error } = useDelivery();
  const { formattedAddress, selectedAddress } = useDeliveryAddressContext();

  // Track when store hours change in real-time to block checkout if store closes
  const [storeHoursChanged, setStoreHoursChanged] = useState(false);

  const [showDeliveryDetails, setShowDeliveryDetails] = useState(false);
  const [calculatingDelivery, setCalculatingDelivery] = useState(false);
  const [vendorQuotes, setVendorQuotes] = useState<VendorQuote[] | null>(null);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const route = useRoute<RouteProp<RootStackParamList, "Cart">>();
  const vendorId = route.params?.vendorId;

  // Get unique vendor IDs from cart items for realtime monitoring
  const cartVendorIds = useMemo(() => {
    const ids = new Set(allCartItems.map((item) => item.product.vendor_id));
    return Array.from(ids);
  }, [allCartItems]);

  // Ref to hold delivery quote refresh function (populated later)
  const refreshDeliveryQuoteRef = useRef<(() => void) | null>(null);

  // Subscribe to real-time vendor delivery_hours changes
  // When a vendor updates their store hours while customer is in cart,
  // we need to re-validate and potentially block checkout
  const { lastUpdate: vendorHoursUpdate, isConnected: hoursRealtimeConnected } = useVendorHoursRealtime({
    vendorIds: cartVendorIds,
    enabled: cartVendorIds.length > 0 && isFocused,
    onHoursChange: useCallback((update) => {
      console.log(`🕒 [CartScreen] Vendor hours changed in real-time:`, {
        vendorId: update.vendorId,
        vendorName: update.vendorName,
        newHours: update.deliveryHours,
      });
      
      // Mark that store hours have changed - this will trigger re-validation
      setStoreHoursChanged(true);
      
      // Show alert to user that store hours have changed
      platformAlert(
        "Store Hours Updated",
        `${update.vendorName || 'The store'} has updated their hours. Your checkout options may have changed.`,
        [
          {
            text: "OK",
            onPress: () => {
              // Refresh delivery quotes to get updated hours validation
              if (refreshDeliveryQuoteRef.current) {
                console.log("[CartScreen] Refreshing delivery quotes after hours change");
                refreshDeliveryQuoteRef.current();
              }
              // Reset the flag after re-fetching
              setStoreHoursChanged(false);
            },
          },
        ]
      );
    }, []),
  });

  // Log realtime connection status for debugging
  useEffect(() => {
    if (cartVendorIds.length > 0) {
      console.log(`🔌 [CartScreen] Vendor hours realtime:`, {
        connected: hoursRealtimeConnected,
        monitoringVendors: cartVendorIds.length,
      });
    }
  }, [hoursRealtimeConnected, cartVendorIds.length]);

  // Query to get user profile for phone validation
  const { data: userProfile } = useQuery({
    queryKey: ["profile", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;
      const { data } = await supabase
        .from("profiles")
        .select("phone_number")
        .eq("id", session.user.id)
        .single();
      return data;
    },
    enabled: !!session?.user?.id,
  });

  // Group cart items by store/vendor
  const itemsByStore = useMemo(() => {
    const grouped = allCartItems.reduce((acc, item) => {
      const storeId = item.product.vendor_id;
      if (!acc[storeId]) {
        acc[storeId] = {
          vendor: item.product.vendor,
          items: [],
        };
      }
      acc[storeId].items.push(item);
      return acc;
    }, {} as Record<string, { vendor: any; items: typeof allCartItems }>);
    return grouped;
  }, [allCartItems]);

  // Get list of stores
  const stores = useMemo(() => {
    return Object.entries(itemsByStore).map(([storeId, data]) => ({
      id: storeId,
      name: data.vendor.store_name,
      address: data.vendor.street_line1,
      itemCount: data.items.length,
    }));
  }, [itemsByStore]);

  // Auto-select store logic
  useEffect(() => {
    // If vendorId is provided via route, use that
    if (vendorId && selectedStoreId !== vendorId) {
      setSelectedStoreId(vendorId);
      return;
    }

    // Auto-select first store if none selected and stores exist
    if (!selectedStoreId && stores.length > 0) {
      setSelectedStoreId(stores[0].id);
    }
  }, [stores, selectedStoreId, vendorId]);

  // Filter cart items based on route vendorId or selected store
  const cartItems = useMemo(() => {
    const targetStoreId = vendorId || selectedStoreId;
    if (!targetStoreId) return allCartItems;

    return allCartItems.filter(
      (item) => item.product.vendor_id === targetStoreId
    );
  }, [allCartItems, vendorId, selectedStoreId]);

  console.log("[CART-FILTER] Store selection debug:", {
    selectedStoreId,
    vendorId,
    filteredCount: cartItems.length,
    totalCount: allCartItems.length,
    storesCount: stores.length,
    storeNames: stores.map((s) => ({ id: s.id, name: s.name })),
    itemsByStoreKeys: Object.keys(itemsByStore),
    selectedStoreInItemsByStore: selectedStoreId
      ? !!itemsByStore[selectedStoreId]
      : false,
    selectedStoreName: selectedStoreId
      ? itemsByStore[selectedStoreId]?.vendor.store_name
      : "none",
    firstCartItemStoreName: cartItems[0]?.product.vendor.store_name,
  });

  // Log initial state
  console.log("CartScreen: Initial State", {
    cartItemsCount: cartItems.length,
    hasDeliveryQuote: !!deliveryQuote,
    hasSelectedAddress: !!selectedAddress,
    isLoading,
    error,
  });

  // Memoize handleGetDeliveryQuote with useCallback
  const memoizedGetDeliveryQuote = useCallback(async () => {
    console.log("CartScreen: Getting delivery quote for filtered items", {
      filteredItemsCount: cartItems.length,
      selectedStoreId,
    });
    try {
      await getQuoteForCart(cartItems); // Pass filtered cart items
      console.log("CartScreen: Successfully got delivery quote");
      setShowDeliveryDetails(true);
    } catch (error) {
      console.error("CartScreen: Delivery quote failed:", error.message);
      if (error.message === "Delivery address not set") {
        console.log("CartScreen: Navigating to DeliveryAddress");
        platformAlert(
          t("cart.deliveryAddressRequired"),
          t("cart.setDeliveryAddressMessage"),
          [
            {
              text: "Set Address",
              onPress: () => {
                console.log("CartScreen: Navigating to DeliveryAddress");
                navigation.navigate("DeliveryAddress");
              },
            },
            { text: t("cart.cancel"), style: "cancel" },
          ]
        );
      } else {
        platformAlert(t("cart.errorTitle"), t("cart.failedDeliveryQuote"));
      }
    }
  }, [
    getQuoteForCart,
    navigation,
    setShowDeliveryDetails,
    cartItems,
    selectedStoreId,
  ]);

  // Keep ref updated for realtime callbacks
  useEffect(() => {
    refreshDeliveryQuoteRef.current = memoizedGetDeliveryQuote;
  }, [memoizedGetDeliveryQuote]);

  // Listen for cart_items deletions (when items are removed by other devices/checkout)
  // Use ref to track current cart items for realtime callbacks
  const cartItemsRef = useRef(cartItems);
  useEffect(() => {
    cartItemsRef.current = cartItems;
  }, [cartItems]);

  useEffect(() => {
    if (!session?.user?.id) return;

    console.log("[CartScreen] Setting up realtime listener for cart deletions");
    const channel = supabase
      .channel(`cart-items-deletions-${session.user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'cart_items',
          filter: `user_id=eq.${session.user.id}`,
        },
        async (payload) => {
          console.log('[CartScreen] Cart item deleted via realtime:', payload.old?.id);
          
          // Check if the deleted item was in our current cart using ref
          const deletedCartItemId = payload.old?.id;
          const deletedProductId = payload.old?.product_id;
          const currentCartItems = cartItemsRef.current;
          const wasInCurrentCart = currentCartItems.some(item => 
            item.id === deletedCartItemId || item.product.id === deletedProductId
          );
          
          // Immediately refresh cart to get latest state
          await refreshCart();
          
          // Show alert if the item was in the current cart
          if (wasInCurrentCart) {
            console.log('[CartScreen] Item in cart was deleted, showing alert');
            // Small delay to ensure navigation/UI updates are complete
            setTimeout(() => {
              platformAlert(
                "Cart Updated",
                "An item in your cart is no longer available and has been removed.",
                [{ text: "Okay", onPress: () => {} }]
              );
            }, 500);
          }
          
          // After refresh, check if cart is empty and navigate
          // The useEffect below will handle navigation when cart becomes empty
          console.log('[CartScreen] Cart refreshed after deletion');
        }
      )
      .subscribe((status) => {
        console.log('[CartScreen] Realtime subscription status:', status);
      });

    return () => {
      console.log("[CartScreen] Cleaning up realtime listener");
      supabase.removeChannel(channel);
    };
  }, [session?.user?.id, refreshCart]);

  // Track if cart was previously populated to avoid navigating on initial empty load
  const [wasCartPopulated, setWasCartPopulated] = React.useState(false);
  const lastNonEmptyCartRef = useRef<any[]>([]);
  
  // Track when cart becomes populated
  useEffect(() => {
    if (allCartItems.length > 0 || cartItems.length > 0) {
      setWasCartPopulated(true);
      // Keep a snapshot of the last non-empty cart for abandoned cart tracking
      lastNonEmptyCartRef.current = cartItems.length > 0 ? cartItems : allCartItems;
    }
  }, [allCartItems, cartItems]);

  // Auto-navigate back to Home when cart becomes empty
  // Only navigate if cart was previously populated (not on initial empty load)
  // AND only when CartScreen is currently focused (not when on CheckoutScreen)
  useEffect(() => {
    // Don't navigate if cart is still loading
    if (isCartLoading) {
      return;
    }

    // Don't navigate if this screen isn't focused
    // This prevents navigation when user is on CheckoutScreen after successful payment
    if (!isFocused) {
      return;
    }

    // Check both allCartItems and filtered cartItems
    const isEmpty = (allCartItems.length === 0 && cartItems.length === 0);
    
    // Only navigate if cart was previously populated and is now empty
    // This prevents navigation on initial load when cart is empty
    if (isEmpty && wasCartPopulated) {
      // Small delay to ensure UI updates are complete
      const timer = setTimeout(() => {
        navigation.navigate('Main' as any);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [allCartItems.length, cartItems.length, wasCartPopulated, isCartLoading, navigation, isFocused]);

  // Use useFocusEffect for screen focus logging and refreshing cart data
  useFocusEffect(
    useCallback(() => {
      console.log("CartScreen: Screen focused", {
        cartItemsCount: cartItems.length,
        hasSelectedAddress: !!selectedAddress,
        hasDeliveryQuote: !!deliveryQuote,
        isLoading,
        error,
      });

      // Refresh cart data when screen is focused to get latest confirmation status
      console.log("CartScreen: Refreshing cart data on focus");
      refreshCart();

      const hasValidQuote = deliveryQuote && deliveryQuote.length > 0 && !error;

      if (
        cartItems.length > 0 &&
        selectedAddress &&
        !hasValidQuote &&
        !isLoading
      ) {
        console.log("CartScreen: Automatically requesting delivery quote");
        memoizedGetDeliveryQuote();
      }

      return () => {
        console.log("CartScreen: Screen unfocused");

        // If the user leaves the cart with items still in it, treat this as a potential abandoned cart
        const lastCart = lastNonEmptyCartRef.current || [];
        if (lastCart.length > 0) {
          try {
            const cartValue = lastCart.reduce(
              (sum, item) => sum + (item.product?.price || 0) * (item.quantity || 0),
              0
            );

            trackEvent(
              "cart_abandoned",
              {
                item_count: lastCart.length,
                cart_value: Number(cartValue.toFixed(2)),
              },
              posthog || undefined
            );
          } catch (analyticsError) {
            console.warn(
              "[CartScreen] Failed to track cart_abandoned:",
              analyticsError
            );
          } finally {
            // Reset snapshot so we don't double count
            lastNonEmptyCartRef.current = [];
          }
        }
      };
    }, [
      cartItems,
      selectedAddress,
      deliveryQuote,
      isLoading,
      error,
      memoizedGetDeliveryQuote,
      refreshCart,
      posthog
    ])
  );

  // Log cart item changes
  useEffect(() => {
    console.log("CartScreen: Cart items changed", {
      itemCount: cartItems.length,
      items: cartItems.map((item) => ({
        id: item.id,
        title: item.product.title,
        quantity: item.quantity,
      })),
    });
  }, [cartItems]);

  // Log delivery quote changes
  useEffect(() => {
    console.log("CartScreen: Delivery quote changed", {
      hasQuote: !!deliveryQuote,
      quoteDetails: deliveryQuote,
      error,
    });
  }, [deliveryQuote, error]);

  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.product.price * item.quantity,
    0
  );

  // Enhanced delivery viability check including store hours validation
  const checkDeliveryViability = useMemo(() => {
    if (!deliveryQuote || deliveryQuote.length === 0) {
      return {
        isViable: false,
        error: "No delivery quotes available",
        fee: 0,
        hasQuoteErrors: false,
        storeHoursIssue: false,
      };
    }

    // Get vendor IDs from filtered cart items
    const cartVendorIds = new Set(
      cartItems
        .filter((item) => {
          if (!selectedStoreId) return true;
          return item.product?.vendor_id === selectedStoreId;
        })
        .map((item) => item.product.vendor_id)
    );

    // Filter delivery quotes to only include vendors from filtered cart
    const relevantQuotes = deliveryQuote.filter((q) =>
      cartVendorIds.has(q.vendorId)
    );

    console.log(
      "💰 [CART-DELIVERY] Calculating delivery viability from filtered quotes:",
      {
        totalQuoteCount: deliveryQuote.length,
        relevantQuoteCount: relevantQuotes.length,
        cartVendorIds: Array.from(cartVendorIds),
        quotes: relevantQuotes.map((q) => ({
          vendorId: q.vendorId,
          hasQuote: !!q.quote,
          hasError: !!q.error,
          fee: q.quote?.fee,
          hasDeliveryHours: !!q.delivery_hours,
        })),
      }
    );

    if (relevantQuotes.length === 0) {
      return {
        isViable: false,
        error: "No delivery quotes for selected vendors",
        fee: 0,
        hasQuoteErrors: false,
        storeHoursIssue: false,
      };
    }

    const successfulQuotes = relevantQuotes.filter((q) => q.quote && !q.error);
    const failedQuotes = relevantQuotes.filter((q) => !q.quote || q.error);

    // Check store hours for all successful quotes
    const storeHoursIssues: { vendorName: string; reason: string }[] = [];
    
    for (const quote of successfulQuotes) {
      // Build vendor location for timezone detection
      const vendorLocation = {
        city: quote.vendor_city,
        state: quote.vendor_state,
        country: quote.vendor_country,
      };
      
      // Check if store is currently open (using store's timezone)
      const storeStatus = isStoreCurrentlyOpen(quote.delivery_hours as StoreHours, vendorLocation);
      
      if (!storeStatus.isOpen) {
        storeHoursIssues.push({
          vendorName: quote.vendor_name,
          reason: storeStatus.reason || 'Store is currently closed',
        });
        console.log(`🕒 [CART-DELIVERY] Store closed: ${quote.vendor_name} - ${storeStatus.reason}`);
        continue;
      }
      
      // Check if delivery would arrive before store closes
      if (quote.quote?.dropoff_eta && quote.delivery_hours) {
        const deliveryTimeValidation = validateDeliveryTimeAgainstStoreHours(
          quote.delivery_hours as StoreHours,
          quote.quote.dropoff_eta,
          0, // buffer minutes
          vendorLocation
        );
        
        if (!deliveryTimeValidation.isValid) {
          storeHoursIssues.push({
            vendorName: quote.vendor_name,
            reason: deliveryTimeValidation.reason,
          });
          console.log(`🕒 [CART-DELIVERY] Delivery time issue: ${quote.vendor_name} - ${deliveryTimeValidation.reason}`);
        }
      }
    }

    // If all quotes have store hours issues, delivery is not viable
    if (storeHoursIssues.length > 0 && storeHoursIssues.length >= successfulQuotes.length) {
      const issueMessage = storeHoursIssues.length === 1
        ? `${storeHoursIssues[0].vendorName}: ${storeHoursIssues[0].reason}`
        : `Store hours issue: ${storeHoursIssues[0].reason}`;
      
      return {
        isViable: false,
        error: issueMessage,
        fee: 0,
        hasQuoteErrors: false,
        storeHoursIssue: true,
      };
    }

    if (failedQuotes.length > 0) {
      console.warn(
        "⚠️ [CART-DELIVERY] Some quotes failed:",
        failedQuotes.map((q) => ({
          vendorId: q.vendorId,
          vendor_name: q.vendor_name,
          error: q.error,
        }))
      );

      // If ALL quotes failed, delivery is not viable
      if (successfulQuotes.length === 0) {
        const errorMessages = failedQuotes
          .map((q) => `${q.vendor_name}: ${q.error || "Quote failed"}`)
          .join("; ");
        return {
          isViable: false,
          error: `Delivery unavailable: seller address not serviceable or outside delivery zone.`,
          fee: 0,
          hasQuoteErrors: true,
          storeHoursIssue: false,
        };
      }

      // If some quotes failed but others succeeded, mark as partially failed
      const errorMessages = failedQuotes
        .map((q) => `${q.vendor_name}: ${q.error || "Quote failed"}`)
        .join("; ");
      console.warn(
        `⚠️ [CART-DELIVERY] Partial delivery failure: ${errorMessages}`
      );
    }

    // Calculate fees only from quotes without store hours issues
    const viableQuotes = successfulQuotes.filter(q => {
      const hasStoreIssue = storeHoursIssues.some(issue => issue.vendorName === q.vendor_name);
      return !hasStoreIssue;
    });

    const totalFeeInCents = viableQuotes.reduce((sum, q) => {
      if (typeof q.quote!.fee === "number") {
        return sum + q.quote!.fee;
      }
      return sum;
    }, 0);

    const totalFeeInDollars = DeliveryHelpers.centsToeDollars(totalFeeInCents);

    console.log("💰 [CART-DELIVERY] Final delivery viability calculation:", {
      successfulQuotes: successfulQuotes.length,
      failedQuotes: failedQuotes.length,
      storeHoursIssues: storeHoursIssues.length,
      viableQuotes: viableQuotes.length,
      totalFeeInCents,
      totalFeeInDollars,
      isViable: viableQuotes.length > 0,
    });

    // Determine final error message
    let errorMessage: string | null = null;
    if (storeHoursIssues.length > 0) {
      errorMessage = `${storeHoursIssues[0].vendorName}: ${storeHoursIssues[0].reason}`;
    } else if (failedQuotes.length > 0) {
      errorMessage = `Some vendors unavailable for delivery`;
    }

    return {
      isViable: viableQuotes.length > 0,
      error: errorMessage,
      fee: totalFeeInDollars,
      hasQuoteErrors: failedQuotes.length > 0,
      storeHoursIssue: storeHoursIssues.length > 0,
    };
  }, [deliveryQuote, cartItems, selectedStoreId, vendorHoursUpdate]);

  const deliveryFee = checkDeliveryViability.fee;
  
  // Calculate buyer fee (15% of order + delivery) after we have deliveryFee
  const buyerFee = DeliveryHelpers.calculateBuyerFee(subtotal, deliveryFee);
  
  console.log("🛒 [CartScreen] Processing fee calculation:", {
    subtotal,
    deliveryFee,
    orderPlusDelivery: subtotal + deliveryFee,
    buyerFeeCalculation: `(${subtotal + deliveryFee}) * ${DELIVERY_CONFIG.fees.BUYER_FEE_PERCENT} = ${buyerFee}`,
    buyerFee
  });
  
  const total = subtotal + deliveryFee + buyerFee;

  const handleUpdateQuantity = (id: string, change: number) => {
    console.log("CartScreen: Updating quantity", { id, change });

    if (!id) {
      console.error("CartScreen: Invalid item ID");
      return;
    }

    const item = cartItems.find((i) => i.id === id);
    if (!item) {
      console.warn("CartScreen: Item not found in cart", id);
      platformAlert(
        "Item Not Found",
        "This item is no longer in your cart. The cart will be refreshed.",
        [{ text: "OK", onPress: () => refreshCart() }]
      );
      return;
    }

    if (!item.product) {
      console.warn("CartScreen: Item has no product data", id);
      platformAlert(
        "Product Unavailable",
        "This product is no longer available and will be removed from your cart.",
        [{ text: "OK", onPress: () => removeItem(id) }]
      );
      return;
    }

    const newQuantity = item.quantity + change;

    // For items with only 1 unit available, block any quantity changes
    if (item.product.quantity === 1) {
      platformAlert(
        "Cannot Change Quantity",
        "This item has only 1 unit available. To remove it from your cart, use the remove button.",
        [{ text: "OK" }]
      );
      return;
    }

    if (newQuantity < 1) {
      platformAlert(
        "Remove Item",
        "Would you like to remove this item from your cart?",
        [
          { text: "Cancel", style: "cancel" },
          {
            text: "Remove",
            style: "destructive",
            onPress: () => handleRemoveItem(id),
          },
        ]
      );
      return;
    }

    if (newQuantity > item.product.quantity) {
      platformAlert(
        "Not Enough Stock",
        `Only ${item.product.quantity} units available for this item.`,
        [{ text: "OK" }]
      );
      return;
    }

    // Update quantity
    try {
      updateQuantity(id, change);
      console.log("CartScreen: Quantity update initiated successfully");
    } catch (error) {
      console.error("CartScreen: Failed to update quantity", error);
      platformAlert(
        "Update Failed",
        "Failed to update item quantity. Please try again.",
        [{ text: "OK" }]
      );
    }
  };

  const handleRemoveItem = (id: string) => {
    console.log("CartScreen: Removing item", { id });

    // Find the item to check if it's confirmed
    const item = cartItems.find((i) => i.id === id);
    if (!item) return;

    if (item.confirmed && !item.pendingConfirmation) {
      // This is a confirmed item - use the special cancellation function
      platformAlert(
        "Cancel Confirmed Item",
        "This item has been confirmed by the seller. Are you sure you want to cancel it? This may affect future orders with this seller.",
        [
          { text: "Keep Item", style: "cancel" },
          {
            text: "Cancel Item",
            style: "destructive",
            onPress: () => cancelConfirmedItem(id),
          },
        ]
      );
    } else if (item.pendingConfirmation) {
      // Item is pending confirmation - don't allow removal
      platformAlert(
        "Cannot Remove Item",
        "This item is waiting for seller confirmation. Please wait for the seller to respond or cancel the entire request.",
        [{ text: "OK" }]
      );
    } else {
      // Regular item - normal removal
      platformAlert(
        "Remove Item",
        "Are you sure you want to remove this item from your cart?",
        [
          { text: "Cancel", style: "cancel" },
          {
            text: "Remove",
            style: "destructive",
            onPress: () => removeItem(id),
          },
        ]
      );
    }
  };

  const handleGetDeliveryQuote = memoizedGetDeliveryQuote;

  // Phone validation function
  const validatePhoneNumber = () => {
    if (!userProfile?.phone_number || userProfile.phone_number.trim() === "") {
      platformAlert(
        "Phone Number Required",
        "A phone number is required to proceed with checkout. Please add your phone number to your profile.",
        [
          {
            text: "Cancel",
            style: "cancel",
          },
          {
            text: "Update Profile",
            onPress: () => navigation.navigate("Profile"),
          },
        ]
      );
      return false;
    }
    return true;
  };

  // Validate inventory before checkout
  const validateInventory = async (): Promise<{ isValid: boolean; validCartItems?: any[] }> => {
    if (cartItems.length === 0) {
      platformAlert("Cart Empty", "Your cart is empty. Please add items before checkout.");
      return { isValid: false };
    }

    // Refresh cart to get latest inventory
    await refreshCart();

    // Wait a bit for the cart state to update
    await new Promise(resolve => setTimeout(resolve, 300));

    // Re-fetch cart items with latest product data (including full product and vendor info)
    const { data: latestCartItems, error: fetchError } = await supabase
      .from('cart_items')
      .select(`
        *,
        product:products(
          id,
          title,
          price,
          quantity,
          status,
          images,
          size,
          vendor_id,
          vendor:vendors(
            id,
            store_name,
            street_line1,
            city,
            state,
            zip_code,
            phone_number,
            latitude,
            longitude
          )
        )
      `)
      .eq('user_id', session?.user?.id || '');

    if (fetchError) {
      console.error('[CartScreen] Failed to validate inventory:', fetchError);
      return { isValid: false };
    }

    if (!latestCartItems || latestCartItems.length === 0) {
      platformAlert("Cart Empty", "Your cart is empty. Please add items before checkout.");
      return { isValid: false };
    }

    const invalidItems: string[] = [];
    const validCartItems: any[] = [];

    for (const item of latestCartItems) {
      const product = item.product;
      if (!product) {
        invalidItems.push(`Item ${item.id} (product not found)`);
        continue;
      }

      const availableQty = Number(product.quantity ?? 0);
      const requestedQty = Number(item.quantity ?? 0);

      if (product.status === 'sold' || product.status === 'archived') {
        invalidItems.push(`${product.title || 'Item'} (no longer available)`);
      } else if (availableQty < requestedQty) {
        invalidItems.push(
          `${product.title || 'Item'} (only ${availableQty} available, ${requestedQty} requested)`
        );
      } else {
        // Item is valid, include it
        validCartItems.push(item);
      }
    }

    if (invalidItems.length > 0) {
      platformAlert(
        "Items No Longer Available",
        `The following items are no longer available:\n\n${invalidItems.join('\n')}\n\nPlease remove them from your cart.`,
        [
          {
            text: "OK",
            onPress: () => refreshCart(),
          },
        ]
      );
      return { isValid: false };
    }

    if (validCartItems.length === 0) {
      platformAlert("Cart Empty", "No valid items in your cart. Please add items before checkout.");
      return { isValid: false };
    }

    return { isValid: true, validCartItems };
  };

  // Modify the handleCheckout function to check for confirmation
  const handleCheckout = async () => {
    console.log("CartScreen: Starting checkout process");
    
    // Track funnel step - begin checkout
    try {
      const checkoutItemCount = cartItems.length;
      const checkoutCartValue = cartItems.reduce(
        (sum, item) => sum + item.product.price * item.quantity,
        0
      );

      trackEvent(
        "checkout_started",
        {
          item_count: checkoutItemCount,
          cart_value: Number(checkoutCartValue.toFixed(2)),
        },
        posthog || undefined
      );
    } catch (analyticsError) {
      console.warn("[CartScreen] Failed to track checkout_started:", analyticsError);
    }

    // Check for phone number first
    if (!validatePhoneNumber()) {
      return;
    }

    if (!selectedAddress) {
      console.log("CartScreen: No delivery address selected");
      platformAlert(
        "Delivery Address Required",
        "Please set your delivery address to continue."
      );
      return;
    }

    // Validate inventory before proceeding
    const validationResult = await validateInventory();
    if (!validationResult.isValid) {
      // If cart became empty, navigate back
      if (cartItems.length === 0) {
        navigation.navigate('Main' as any);
      }
      return;
    }

    // Use validated cart items (with full product/vendor data) for delivery quote
    const itemsForQuote = validationResult.validCartItems || cartItems;

    if (itemsForQuote.length === 0) {
      platformAlert("Cart Empty", "Your cart is empty. Please add items before checkout.");
      navigation.navigate('Main' as any);
      return;
    }

    setCalculatingDelivery(true);
    console.log("CartScreen: Calculating final delivery quote", {
      itemsCount: itemsForQuote.length,
      itemIds: itemsForQuote.map(i => i.id),
    });

    try {
      await getQuoteForCart(itemsForQuote); // Pass validated cart items with full data

      if (error) {
        console.error("CartScreen: Checkout error", error);
        platformAlert("Error", error || "Failed to get delivery quote");
        return;
      }

      console.log("CartScreen: Navigating to checkout", {
        total,
        deliveryFee,
        cartItemsCount: cartItems.length,
      });

      navigation.navigate("Checkout", {
        total: total,
        cartItems,
        deliveryFee: deliveryFee,
        deliveryQuote: deliveryQuote,
        deliveryAddress: {
          street_address: [
            selectedAddress.street_address +
              (selectedAddress.apartment
                ? ` #${selectedAddress.apartment}`
                : ""),
          ],
          city: selectedAddress.city || "",
          state: selectedAddress.state || "NY",
          zip_code: selectedAddress.zip_code || "",
          country: "US",
        },
      });
    } catch (err) {
      console.error("CartScreen: Checkout failed", err);
      platformAlert(
        "Error",
        err instanceof Error
          ? err.message
          : "Failed to calculate delivery quote"
      );
    } finally {
      setCalculatingDelivery(false);
    }
  };

  // Render store selector
  const renderStoreSelector = () => {
    if (stores.length <= 1 || vendorId) return null; // Hide if only one store or vendor specified via route

    return (
      <View style={styles.storeSelectorContainer}>
        <Text style={styles.storeSelectorTitle}>Select Store:</Text>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.storeSelectorScroll}
        >
          {stores.map((store) => (
            <TouchableOpacity
              key={store.id}
              style={[
                styles.storeOption,
                selectedStoreId === store.id && styles.storeOptionSelected,
              ]}
              onPress={() => setSelectedStoreId(store.id)}
            >
              <Text
                style={[
                  styles.storeOptionText,
                  selectedStoreId === store.id &&
                    styles.storeOptionTextSelected,
                ]}
              >
                {store.name}
              </Text>
              <Text
                style={[
                  styles.storeItemCount,
                  selectedStoreId === store.id && styles.storeItemCountSelected,
                ]}
              >
                {store.itemCount} item{store.itemCount !== 1 ? "s" : ""}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    );
  };

  const renderCartItem = (item) => {
    // Safety check - ensure item and product exist
    if (!item || !item.product) {
      console.warn("CartScreen: renderCartItem called with invalid item", item);
      return null;
    }

    const product = item.product;
    const vendor = product.vendor;

    if (!vendor) {
      console.warn(
        "CartScreen: renderCartItem - product has no vendor data",
        product
      );
      return null;
    }

    return (
      <View key={item.id} style={styles.cartItem}>
        <TouchableOpacity
          style={styles.itemContent}
          onPress={() =>
            navigation.navigate("ProductDetails", { id: product.id })
          }
        >
          <Image
            source={{
              uri:
                product.images && product.images[0]
                  ? product.images[0]
                  : "https://via.placeholder.com/80x80?text=No+Image",
            }}
            style={[
              styles.itemImage,
              (!product.images || !product.images[0]) &&
                styles.placeholderImage,
            ]}
            contentFit="cover"
            cachePolicy="memory-disk"
            transition={0}
          />

          <View style={styles.itemDetails}>
            <View style={styles.itemTitleRow}>
              <Text style={styles.itemTitle}>
                {product.title || "Unknown Product"}
              </Text>
            </View>

            <Text style={styles.vendorName}>
              {item.product.vendor.store_name}
            </Text>
            {item.product.size && (
              <Text style={styles.itemSize}>Size: {item.product.size}</Text>
            )}
            <Text style={styles.itemPrice}>
              {formatPrice(item.product.price)}
            </Text>

            <View style={styles.quantityControl}>
              {/* Hide quantity controls for single-unit items */}
              {item.product.quantity > 1 ? (
                <>
                  {item.quantity > 1 && (
                    <TouchableOpacity
                      onPress={(e) => {
                        e.stopPropagation();
                        handleUpdateQuantity(item.id, -1);
                      }}
                      style={styles.quantityButton}
                    >
                      <Feather name="minus" size={16} color={colors.text} />
                    </TouchableOpacity>
                  )}

                  <Text style={styles.quantity}>{item.quantity}</Text>

                  {item.product.quantity > item.quantity && (
                    <TouchableOpacity
                      onPress={(e) => {
                        e.stopPropagation();
                        handleUpdateQuantity(item.id, 1);
                      }}
                      style={styles.quantityButton}
                    >
                      <Feather name="plus" size={16} color={colors.text} />
                    </TouchableOpacity>
                  )}
                </>
              ) : (
                <View style={styles.singleUnitIndicator}>
                  <Text style={styles.singleUnitText}>1 unit only</Text>
                </View>
              )}
            </View>
          </View>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={() => handleRemoveItem(item.id)}
          style={styles.removeButton}
          disabled={isRemoving || isCancelling}
        >
          {isRemoving || isCancelling ? (
            <ActivityIndicator size="small" color={colors.textLight} />
          ) : (
            <Feather name="x" size={20} color={colors.text} />
          )}
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <ScrollView>
        {cartItems.length === 0 && (
          <View style={styles.emptyState}>
            <Feather name="shopping-cart" size={64} color={colors.textLight} />
            <Text style={styles.emptyTitle}>Your cart is empty</Text>
            <Text style={styles.emptyText}>
              Start shopping to add items to your cart
            </Text>
            <Button
              onPress={() => navigation.navigate("Browse", {})}
              style={styles.browseButton}
            >
              Browse Products
            </Button>
          </View>
        )}

        {renderStoreSelector()}

        {/* Show selected store info for single store or when store is selected */}
        {cartItems.length > 0 && (
          <View style={styles.selectedStoreInfo}>
            <Feather name="shopping-bag" size={16} color={colors.primary} />
            <Text style={styles.selectedStoreText}>
              Shopping from:{" "}
              {(() => {
                // Get the store name from the actual filtered cart items (most reliable)
                const firstCartItem = cartItems[0];
                if (firstCartItem) {
                  return firstCartItem.product.vendor.store_name;
                }

                // Fallback to itemsByStore if cart is somehow empty
                if (selectedStoreId && itemsByStore[selectedStoreId]) {
                  return itemsByStore[selectedStoreId].vendor.store_name;
                }

                return "Unknown store";
              })()}
            </Text>
            {stores.length > 1 && (
              <Text style={styles.selectedStoreSubtext}>
                {cartItems.length} item{cartItems.length !== 1 ? "s" : ""} in
                cart
              </Text>
            )}
          </View>
        )}

        {cartItems.length > 0 && (
          <View>{cartItems.map((item) => renderCartItem(item))}</View>
        )}

        {cartItems.length > 0 && (
          <View style={styles.deliverySection}>
            <TouchableOpacity
              style={[
                styles.deliveryToggle,
                error ? styles.deliveryToggleError : null,
              ]}
              onPress={() => {
                if (!deliveryQuote) {
                  handleGetDeliveryQuote();
                } else {
                  setShowDeliveryDetails(!showDeliveryDetails);
                }
              }}
              disabled={isLoading}
            >
              <View style={styles.deliveryToggleContent}>
                <Text style={styles.deliveryToggleText}>
                  {isLoading
                    ? "Calculating Delivery..."
                    : error
                    ? "Retry Delivery Quote"
                    : deliveryQuote
                    ? (() => {
                        // Filter quotes for current store only
                        const cartVendorIds = new Set(
                          cartItems.map((item) => item.product.vendor_id)
                        );
                        const relevantQuotes = deliveryQuote.filter((q) =>
                          cartVendorIds.has(q.vendorId)
                        );
                        const successfulQuotes = relevantQuotes.filter(
                          (q) => q.quote && !q.error
                        );
                        const failedQuotes = relevantQuotes.filter(
                          (q) => !q.quote || q.error
                        );

                        if (
                          failedQuotes.length > 0 &&
                          successfulQuotes.length > 0
                        ) {
                          return `Partial Delivery Available (${successfulQuotes.length}/${relevantQuotes.length})`;
                        } else if (failedQuotes.length > 0) {
                          return "Delivery Issues Found";
                        } else {
                          return "Delivery Available";
                        }
                      })()
                    : "Calculate Delivery"}
                </Text>
                {error && (
                  <Text style={styles.errorText} numberOfLines={2}>
                    {error}
                  </Text>
                )}
                {deliveryQuote &&
                  !error &&
                  (() => {
                    // Filter quotes for current store only
                    const cartVendorIds = new Set(
                      cartItems.map((item) => item.product.vendor_id)
                    );
                    const relevantQuotes = deliveryQuote.filter((q) =>
                      cartVendorIds.has(q.vendorId)
                    );
                    const failedQuotes = relevantQuotes.filter(
                      (q) => !q.quote || q.error
                    );
                    return (
                      failedQuotes.length > 0 && (
                        <Text style={styles.warningText} numberOfLines={2}>
                          Some vendors unavailable for delivery
                        </Text>
                      )
                    );
                  })()}
              </View>
              {deliveryQuote && !error && (
                <Text style={styles.deliveryPrice}>
                  {formatPrice(deliveryFee)}
                </Text>
              )}
              {isLoading && (
                <ActivityIndicator
                  size="small"
                  color={colors.primary}
                  style={styles.loader}
                />
              )}
            </TouchableOpacity>

            {showDeliveryDetails && deliveryQuote && (
              <View style={styles.deliveryDetails}>
                <CartDeliveryMap
                  userLocation={
                    selectedAddress &&
                    selectedAddress.latitude &&
                    selectedAddress.longitude
                      ? {
                          lat: selectedAddress.latitude,
                          lng: selectedAddress.longitude,
                        }
                      : undefined
                  }
                  deliveryAddress={formattedAddress}
                />
                {deliveryQuote
                  .filter((quote) => {
                    const cartVendorIds = new Set(
                      cartItems.map((item) => item.product.vendor_id)
                    );
                    return cartVendorIds.has(quote.vendorId);
                  })
                  .map((quote, index) => {
                    // Find the vendor information from cart items
                    const vendorId = quote.vendorId;
                    const vendorItem = cartItems.find(
                      (item) => item.product.vendor_id === vendorId
                    );
                    const vendorAddress =
                      vendorItem?.product.vendor.street_line1 ||
                      "Unknown Location";

                    // Handle failed quotes
                    if (quote.error || !quote.quote) {
                      return (
                        <View
                          key={index}
                          style={[styles.quoteDetail, styles.quoteError]}
                        >
                          <Text style={styles.vendorName}>
                            {quote.vendor_name}
                          </Text>
                          <Text style={styles.errorText}>
                            ❌ Delivery unavailable: seller address not
                            serviceable or outside delivery zone.
                            {/* {quote.error || "Quote failed"} */}
                          </Text>
                        </View>
                      );
                    }

                    return (
                      <View key={index} style={styles.quoteDetail}>
                        <Text style={styles.vendorName}>
                          From: {quote.vendor_name}
                        </Text>
                        <Text style={styles.deliveryTime}>
                          Estimated delivery:{" "}
                          {calculateDeliveryTime(quote.quote.dropoff_eta)}
                        </Text>
                        <Text style={styles.quoteFee}>
                          Fee:{" "}
                          {formatPrice(DeliveryHelpers.centsToeDollars(
                            quote.quote.fee
                          ))}
                        </Text>
                      </View>
                    );
                  })}
              </View>
            )}
          </View>
        )}
      </ScrollView>

      {cartItems.length > 0 && (
        <View style={styles.summary}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Subtotal</Text>
            <Text style={styles.summaryValue}>{formatPrice(subtotal)}</Text>
          </View>

          {deliveryFee > 0 && (
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Delivery</Text>
              <Text style={styles.summaryValue}>{formatPrice(deliveryFee)}</Text>
            </View>
          )}

          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>
              Processing Fee
            </Text>
            <Text style={styles.summaryValue}>{formatPrice(buyerFee)}</Text>
          </View>

          <View style={[styles.summaryRow, styles.totalRow]}>
            <Text style={styles.totalLabel}>Total</Text>
            <Text style={styles.totalValue}>{formatPrice(total)}</Text>
          </View>

          {!checkDeliveryViability.isViable &&
            checkDeliveryViability.error &&
            !isLoading &&
            !calculatingDelivery && (
              <View style={[styles.quoteError, { marginBottom: 12 }]}>
                <Text style={styles.errorText}>
                  {checkDeliveryViability.storeHoursIssue ? '🕒' : '⚠️'} Cannot proceed with checkout:{" "}
                  {checkDeliveryViability.error}
                </Text>
              </View>
            )}

          <TouchableOpacity
            style={[
              styles.actionButton,
              !checkDeliveryViability.isViable
                ? styles.disabledButton
                : styles.confirmButton,
            ]}
            onPress={() => {
              if (!checkDeliveryViability.isViable) {
                console.warn(
                  "Cannot proceed: Delivery not viable",
                  checkDeliveryViability.error
                );
                return;
              }
              handleCheckout();
            }}
            disabled={Boolean(
              !checkDeliveryViability.isViable ||
                isLoading ||
                calculatingDelivery
            )}
          >
            {isLoading || calculatingDelivery ? (
              <ActivityIndicator color={colors.surface} size="small" />
            ) : !checkDeliveryViability.isViable ? (
              <Text style={styles.buttonText}>
                {checkDeliveryViability.storeHoursIssue ? 'Store Closed' : 'Delivery Unavailable'}
              </Text>
            ) : (
              <Text style={styles.buttonText}>Proceed to Checkout</Text>
            )}
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}
